import { AlertColor, Box, Card, Grid } from '@mui/material';
import TitleText from '../../components/formlib/TitleText';
import MediumTypography from '../../components/formlib/MediumTypography';
import Textinput from '../../components/formlib/Textinput';
import RadioComponent from '../../components/formlib/RadioComponent';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { Formik, FormikErrors, FormikProps } from 'formik';
import {
  MileageRate,
  GetProgramConfigurationType,
  UpdateProgramConfigurationType,
} from '../../utils/type';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { editOrAddProgramConfiigurationApi } from '../../services/programManagment/programConfiguration';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { getProgramConfigurationApi } from './../../services/programManagment/programConfiguration';
import _ from 'lodash';
import { PROGRAM_MANAGEMENT_ROUTE_NAME } from '../../routes/Routing';
import { useNavigate } from 'react-router';
import ModalPopup from '../../components/formlib/ModalPopup';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import { ReactComponent as DeleteIcon } from '../../assets/images/deleteAlertIcon.svg';

import dayjs from 'dayjs';
import { STORAGE_USER_ID_KEY } from '../../services/Constant';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';

interface ProgramConfigProp {
  programId: string;
}

const initialValues: GetProgramConfigurationType = {
  dueDay: '',
  mileageRates: [
    {
      mileageRate: '',
      mileageRateId: '',
      startDate: '',
      endDate: null,
    },
  ],
};

const validationSchema = yup.object().shape({
  dueDay: yup.string().nullable().required('Program.Configuration.dueDayError'),
  mileageRates: yup.array().of(
    yup.object().shape({
      mileageRate: yup
        .string()
        .required('Program.Configuration.MileageTextError')
        .nullable(),
      startDate: yup.string().required('startDateRequiredMessage').nullable(),
    }),
  ),
});

interface UnsavedChangesWatcherProps {
  values: GetProgramConfigurationType;
  initialProgramConfigurationValue: GetProgramConfigurationType;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
}

const UnsavedChangesWatcher = ({
  values,
  initialProgramConfigurationValue,
  onUnsavedChangesChange,
}: UnsavedChangesWatcherProps) => {
  useEffect(() => {
    if (_.isEqual(initialProgramConfigurationValue, values)) {
      onUnsavedChangesChange(false);
    } else {
      onUnsavedChangesChange(true);
    }
  }, [values]);

  return null;
};

const ProgramConfiguration: FC<ProgramConfigProp> = ({ programId }) => {
  const formRef = useRef<FormikProps<GetProgramConfigurationType>>(null);
  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [
    initialProgramConfigurationValue,
    setInitialProgramConfigurationValue,
  ] = useState<GetProgramConfigurationType>(initialValues);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const navigate = useNavigate();
  const [showMileageRatePopup, setShowMileageRatePopup] = useState(false);
  const selectedProgram: string | null =
    localStorage.getItem('selectedProgram');
  const selectedProgramTitle: string | null = selectedProgram
    ? JSON.parse(selectedProgram).label
    : '';
  const superProgramId: string | null = localStorage.getItem('superProgramId');
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;

  const permissions = useMultiplePermissions(['backend.mileage_rate']);
  const hasMileageRateEditPermission =
    permissions['backend.mileage_rate'].canEdit;

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleAddOrEdit = (
    latestRate: MileageRate,
    dueDate: string,
    updateTravelExpenses: boolean,
  ) => {
    const requestBody: UpdateProgramConfigurationType = {
      dueDay: dueDate,
      id: latestRate.mileageRateId,
      mileageRate: latestRate.mileageRate,
      startDate: latestRate.startDate,
      employeeId: localStorage.getItem(STORAGE_USER_ID_KEY) ?? '',
      updateTravelExpenses,
    };

    toggleLoader(true);
    editOrAddProgramConfiigurationApi(programId, requestBody)
      .then(() => {
        toggleLoader(false);
        setOpen(true);
        setToastrVariable('success');
        setShowMileageRatePopup(false);
        setToastrId('Program.Configuration.successmsg');
        setToastrDefaultMessage('Saved Successfully');
        getProgramConfiguration();
      })
      .catch((error) => {
        toggleLoader(false);
        setShowMileageRatePopup(false);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else if (error.code === 1152) {
          setToastrId('Program.Configuration.dateErrormsg');
          setToastrDefaultMessage('Please enter a valid start date');
        } else {
          setToastrId('Program.Configuration.errormsg');
          setToastrDefaultMessage('Failed to Configure Program');
        }
      });
  };

  const getProgramConfiguration = () => {
    getProgramConfigurationApi(programId)
      .then((response) => {
        setInitialProgramConfigurationValue(response);
        formRef.current?.setValues(response);
      })
      .catch((error) => {
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('Program.Configuration.errormsg');
          setToastrDefaultMessage('Failed to Configure Program');
        }
      });
  };

  useEffect(() => {
    getProgramConfiguration();
  }, []);

  const clearForm = () => {
    formRef.current?.setValues(initialProgramConfigurationValue);
    setOpenModal(false);
  };

  const handleBlur = (newValue: string, index: number) => {
    let formattedValue = newValue;
    if (newValue.endsWith('.')) {
      formattedValue = newValue + '0';
    }

    if (newValue.startsWith('.')) {
      formattedValue = '0' + newValue;
    }

    if (newValue === '.') {
      formattedValue = '0.0';
    }

    formRef.current?.setFieldValue(
      `mileageRates.${index}.mileageRate`,
      formattedValue,
    );
  };

  return (
    <Box component="div">
      <SnackBarComponent
        open={open}
        handleClose={handleClose}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <ModalPopup
        description="formUnsavedChangesMessage"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={() => clearForm()}
        labelId1="Clientpage.Nobtn"
        negativeActionLabel="No"
        labelId2="Clientpage.Yesbtn"
        positiveActionLabel="Yes"
      />
      <ModalPopup
        description="mileageRatePopupDescText"
        defaultDescription="All Non-approved expenses will be updated with the new rate, effective from the selected Start Date. Do you want to proceed?"
        open={showMileageRatePopup}
        onCancel={() => setShowMileageRatePopup(false)}
        onOk={() => {
          if (formRef.current) {
            const values = structuredClone(formRef.current.values);
            const latestRate = values.mileageRates.at(-1);
            if (!latestRate) {
              return;
            }
            setInitialProgramConfigurationValue(values);
            handleAddOrEdit(latestRate, values.dueDay, true);
          }
        }}
        labelId1="Clientpage.Nobtn"
        negativeActionLabel="No"
        labelId2="Clientpage.Yesbtn"
        positiveActionLabel="Yes"
      />
      <Formik
        innerRef={formRef}
        // validateOnMount={true}
        // enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          if (values.mileageRates.length === 1) {
            return;
          } else {
            const valuesCopy = structuredClone(values);
            const latestRate = valuesCopy.mileageRates.at(-1);
            if (!latestRate) {
              return;
            }
            if (latestRate.mileageRateId === '') {
              setShowMileageRatePopup(true);
            } else {
              setInitialProgramConfigurationValue(values);
              handleAddOrEdit(latestRate, values.dueDay, false);
            }
          }
        }}
      >
        {({ setFieldValue, handleSubmit, values, errors, touched }) => {
          return (
            <Box>
              <UnsavedChangesWatcher
                values={values}
                initialProgramConfigurationValue={
                  initialProgramConfigurationValue
                }
                onUnsavedChangesChange={handleUpdateUnsavedChanges}
              />
              <Card className="formCardview">
                <TitleText
                  label={selectedProgramTitle}
                  Sxprops={{ fontSize: '20px' }}
                />
                <Box className="horizontalLine" />
                <Box>
                  <MediumTypography
                    labelid="program.Configuration.Accruals"
                    defaultlabel="Accruals"
                    fontSize="18px"
                    fontweight={600}
                  />
                  <Box
                    className="flex__ "
                    sx={{
                      paddingTop: '24px',
                    }}
                  >
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.Accruals.VacationAccrueRate"
                        defaultlabel="Vacation Accrue Rate"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        className=""
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="Enter"
                      />
                      <MediumTypography labelid="persent" defaultlabel="%" />
                    </Box>
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                        paddingLeft: '40px',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.Accruals.SickTimeAccrueRate"
                        defaultlabel="Sick Time Accrue Rate"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="Enter"
                      />
                      <MediumTypography labelid="Percent" defaultlabel="%" />
                    </Box>
                  </Box>
                </Box>
                <Box className="horizontalLine" />
                <Box>
                  <MediumTypography
                    labelid="Program.Configuration.CarryOvers"
                    defaultlabel="Carry overs"
                    fontSize="18px"
                    fontweight={600}
                  />
                  <Box
                    className="flex__ "
                    sx={{
                      paddingTop: '24px',
                    }}
                  >
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.CarryOvers.Vacationcarryoverallowed"
                        defaultlabel="Vacation carryover allowed"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="0"
                      />
                      <MediumTypography
                        labelid="Program.Configuration.Hours"
                        defaultlabel="hrs"
                      />
                    </Box>
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                        paddingLeft: '40px',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.CarryOvers.SickTimeCarryoverAllowed"
                        defaultlabel="Sick time carryover allowed"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="0"
                      />
                      <MediumTypography
                        labelid="Program.Configuration.Hours"
                        defaultlabel="hrs"
                      />
                    </Box>
                  </Box>
                  <Box
                    className="flex__ "
                    sx={{
                      paddingTop: '24px',
                    }}
                  >
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.CarryOvers.ProfessionalLeaveCarryoverAllowed"
                        defaultlabel="Professional leave carryover allowed"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="0"
                      />
                      <MediumTypography
                        labelid="Program.Configuration.Hours"
                        defaultlabel="hrs"
                      />
                    </Box>
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                        paddingLeft: '40px',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.CarryOvers.HolidayHoursCarryoverAllowed"
                        defaultlabel="Holiday hours carryover allowed"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="0"
                      />
                      <MediumTypography
                        labelid="Program.Configuration.Hours"
                        defaultlabel="hrs"
                      />
                    </Box>
                  </Box>
                  <Box
                    className="flex__ "
                    sx={{
                      paddingTop: '24px',
                    }}
                  >
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.CarryOvers.FlexHoursCarryoverAllowed"
                        defaultlabel="Flex hours carryover allowed"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="0"
                      />
                      <MediumTypography
                        labelid="Program.Configuration.Hours"
                        defaultlabel="hrs"
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className="horizontalLine" />
                <Box>
                  <MediumTypography
                    labelid="Program.Configuration.Add-Ons"
                    defaultlabel="Add-ons"
                    fontSize="18px"
                    fontweight={600}
                  />
                  <Box
                    className="flex__ "
                    sx={{
                      paddingTop: '24px',
                    }}
                  >
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.Add-Ons.Non-Direct"
                        defaultlabel="Non-Direct"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="0"
                      />
                    </Box>
                    <Box
                      className="flex__ "
                      sx={{
                        alignItems: 'center',
                        paddingLeft: '40px',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.Add-Ons.Non-Exempt"
                        defaultlabel="Non-Exempt"
                        sxProps={{ whiteSpace: 'nowrap' }}
                      />
                      <Textinput
                        sxProps={{ paddingLeft: '16px', paddingRight: '8px' }}
                        placeHolder="0"
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className="horizontalLine" />

                <Box>
                  <MediumTypography
                    labelid="Program.Configuration.WeeklyActivitySheet"
                    defaultlabel="Weekly activity sheet"
                    fontSize="18px"
                    fontweight={600}
                  />
                  <Box
                    sx={{
                      paddingTop: '24px',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      className="flex__"
                      sx={{
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <MediumTypography
                        labelid="Program.Configuration.WeeklyActivitySheet.DueDate"
                        defaultlabel="Due Day"
                        sxProps={{ paddingRight: '16px', whiteSpace: 'nowrap' }}
                      />

                      <Box sx={{ width: '250px' }}>
                        <DropdownComponent
                          labelid="Program.Configuration.WeeklyActivitySheet.-Select-"
                          defaultlabelid="-select-"
                          names={[
                            { id: '0', label: 'Sunday' },
                            { id: '1', label: 'Monday' },
                            { id: '2', label: 'Tuesday' },
                            { id: '3', label: 'Wednesday' },
                            { id: '4', label: 'Thursday' },
                            { id: '5', label: 'Friday' },
                            { id: '6', label: 'Saturday' },
                          ]}
                          value={values.dueDay}
                          disabled={
                            !(selectedProgram
                              ? JSON.parse(selectedProgram).id ===
                                superProgramId
                              : false)
                          }
                          handleChange={(e) => {
                            setFieldValue('dueDay', e);
                          }}
                        />
                      </Box>
                    </Box>
                    {errors.dueDay && touched.dueDay && (
                      <MediumTypography
                        className="errorText-md"
                        labelid={errors.dueDay}
                        defaultlabel="Due Day is Required"
                      />
                    )}
                  </Box>
                </Box>
                <Box className="horizontalLine" />

                <Box>
                  <MediumTypography
                    className="mb-md"
                    labelid="Program.Configuration.TravelExpensesText"
                    defaultlabel="Add Expenses"
                    fontSize="18px"
                    fontweight={600}
                  />

                  <Grid container rowSpacing={1}>
                    {values.mileageRates.map((mileageRate, index) => (
                      <Grid
                        key={mileageRate.mileageRateId}
                        container
                        direction="row"
                        xs={9}
                        columnSpacing={2}
                      >
                        <Grid item xs={4} className="mt-md">
                          <Textinput
                            Value={mileageRate.mileageRate}
                            handlechange={(text: string) => {
                              const newValue = text
                                .trimStart()
                                .replaceAll(/[^\d.]/g, '')
                                .replace(/^(\d{0,2})(\.\d{0,4})?.*$/, '$1$2');
                              setFieldValue(
                                `mileageRates.${index}.mileageRate`,
                                newValue,
                              );
                            }}
                            labelid="travelExpenses.milage"
                            placeHolder="Mileage Rate ($)"
                            onblur={(event) =>
                              handleBlur(event.target.value.trim(), index)
                            }
                            disabled={
                              !hasMileageRateEditPermission ||
                              values.mileageRates.length - 1 !== index ||
                              JSON.parse(selectedProgram ?? '').id !==
                                superProgramId
                            }
                          />
                          {errors.mileageRates &&
                            touched.mileageRates &&
                            (
                              errors.mileageRates[
                                index
                              ] as FormikErrors<MileageRate>
                            )?.mileageRate && (
                              <MediumTypography
                                className="errorText-md"
                                labelid={
                                  (
                                    errors.mileageRates[
                                      index
                                    ] as FormikErrors<MileageRate>
                                  )?.mileageRate
                                }
                                defaultlabel="Mileage Rate is required"
                              />
                            )}
                        </Grid>

                        <Grid item xs={4} className="mt-md">
                          <DatePickerComponent
                            labelid="startDateText"
                            defaultlabelid="Start Date"
                            required={true}
                            minDate={
                              values.mileageRates[index - 1]?.startDate
                                ? dayjs(
                                    values.mileageRates[index - 1]?.startDate,
                                  ).add(1, 'day')
                                : undefined
                            }
                            value={
                              mileageRate.startDate
                                ? dayjs(mileageRate.startDate)
                                : null
                            }
                            disabledDate={
                              !hasMileageRateEditPermission ||
                              values.mileageRates.length - 1 !== index ||
                              JSON.parse(selectedProgram ?? '').id !==
                                superProgramId
                            }
                            handlechange={(date) => {
                              if (date === null) {
                                setFieldValue(
                                  `mileageRates.${index}.startDate`,
                                  null,
                                );
                              } else {
                                const formattedDate =
                                  dayjs(date).format('MM/DD/YYYY');
                                setFieldValue(
                                  `mileageRates.${index}.startDate`,
                                  formattedDate,
                                );
                              }
                            }}
                          />
                          {errors.mileageRates &&
                            touched.mileageRates &&
                            (
                              errors.mileageRates[
                                index
                              ] as FormikErrors<MileageRate>
                            )?.startDate && (
                              <MediumTypography
                                className="errorText-md"
                                labelid={
                                  (
                                    errors.mileageRates[
                                      index
                                    ] as FormikErrors<MileageRate>
                                  )?.startDate
                                }
                                defaultlabel="Start Date is required"
                              />
                            )}
                        </Grid>

                        {index !== 0 &&
                          values.mileageRates.length - 1 === index &&
                          values.mileageRates[index].mileageRateId === '' && (
                            <Grid item xs={1}>
                              <Box
                                className="mt-lg cursorPointer"
                                onClick={() => {
                                  const newValues = [...values.mileageRates];
                                  newValues.splice(index, 1);
                                  setFieldValue('mileageRates', newValues);
                                }}
                              >
                                <DeleteIcon />
                              </Box>
                            </Grid>
                          )}
                      </Grid>
                    ))}
                  </Grid>

                  {hasMileageRateEditPermission &&
                    selectedProgram &&
                    JSON.parse(selectedProgram).id === superProgramId &&
                    values.mileageRates.at(-1)?.mileageRateId !== '' && (
                      <ButtonComponent
                        type="submit"
                        variantType="contained"
                        defaultLabelId="+ Add New"
                        labelId="IFSP.AddNewSection"
                        className="btn-primary btn-submit mt-md"
                        onClick={() => {
                          const hasError = errors.mileageRates?.at(
                            -1,
                          ) as FormikErrors<MileageRate>;
                          if (hasError) {
                            return;
                          }
                          const mileageRates = values.mileageRates;
                          mileageRates.push({
                            mileageRateId: '',
                            startDate: '',
                            endDate: null,
                            mileageRate: '',
                          });
                          setFieldValue('mileageRates', mileageRates);
                        }}
                      />
                    )}
                </Box>
                <Box className="horizontalLine" />

                <Box>
                  <Box
                    className="flex__"
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      labelid="Program.Configuration.DefaultServiceVerificationMethod"
                      defaultlabel="Default service verification method"
                      fontSize="18px"
                      fontweight={600}
                      sxProps={{
                        paddingRight: '70px',
                        whiteSpace: 'nowrap',
                      }}
                    />
                    <RadioComponent
                      labelidA="Program.Configuration.RadioA"
                      defaultlabelidA="Phone"
                      labelidB="Program.Configuration.RadioB"
                      defaultlabelidB="Email"
                    />
                  </Box>
                </Box>
                <Box className="horizontalLine" />
                <Box>
                  <Box
                    className="flex__"
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      labelid="Program.Configuration.NumberOfClinicians"
                      defaultlabel="Service verification / Quality assurance: Number of clinicians to verify per month"
                      fontSize="18px"
                      fontweight={600}
                      sxProps={{
                        whiteSpace: 'nowrap',
                      }}
                    />
                    <Textinput
                      sxProps={{
                        paddingLeft: '40px',
                        width: '16%',
                      }}
                      placeHolder="Enter"
                    />
                  </Box>
                </Box>
                <Box className="horizontalLine" />
                <Box>
                  <Box
                    className="flex__"
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      labelid="Program.Configuration.NumberOfClients"
                      defaultlabel="Service verification / Quality assurance: Number of clients to verify per month"
                      fontSize="18px"
                      fontweight={600}
                      sxProps={{
                        whiteSpace: 'nowrap',
                      }}
                    />
                    <Textinput
                      sxProps={{
                        paddingLeft: '40px',
                        width: '16%',
                      }}
                      placeHolder="Enter"
                    />
                  </Box>
                </Box>
                <Box className="horizontalLine" />
                <Box>
                  <Box
                    className="flex__"
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <MediumTypography
                      labelid="Program.Configuration.ClientUniquenessTimeframe"
                      defaultlabel="Service verification / Quality assurance: Client uniqueness timeframe in number of month"
                      fontSize="18px"
                      fontweight={600}
                      sxProps={{
                        whiteSpace: 'nowrap',
                      }}
                    />
                    <Textinput
                      sxProps={{
                        paddingLeft: '40px',
                        width: '16%',
                      }}
                      placeHolder="Enter"
                    />
                  </Box>
                </Box>
              </Card>
              <Box>
                <Grid
                  container
                  direction="row"
                  alignItems="right"
                  className="flex__ justifyContent-FlexEnd pt-md pb-lg "
                >
                  <Grid item>
                    <ButtonComponent
                      className="btn-primary btn-cancel"
                      variantType="contained"
                      labelId="Contacts.cancelbtn"
                      defaultLabelId="Cancel"
                      onClick={() => {
                        if (
                          _.isEqual(initialProgramConfigurationValue, values)
                        ) {
                          navigate(PROGRAM_MANAGEMENT_ROUTE_NAME);
                        } else {
                          setOpenModal(true);
                        }
                      }}
                    />
                  </Grid>

                  <Grid item>
                    <ButtonComponent
                      className="btn-primary btn-submit ml-md"
                      variantType="contained"
                      type="submit"
                      labelId="parentGuardian.save"
                      defaultLabelId="Save & Submit"
                      onClick={handleSubmit}
                      disabled={!hasMileageRateEditPermission}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};

export default ProgramConfiguration;
